import { parseDbTime } from "@nexus/widget-kit";
import { useDisplayTimezone } from "@nexus/ui";
import { formatCount, formatDateTime } from "./format";
import type { MonitoringSegment } from "./model";

/** Cumulative detections summed across the segments that report a trend. */
export function combinedTrend(segments: MonitoringSegment[]) {
  const populated = segments.filter((segment) => segment.trend.length > 0);
  const times = Array.from(
    new Set(populated.flatMap((segment) => segment.trend.map((point) => parseDbTime(point.at)))),
  )
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  return times.flatMap((at) => {
    const values = populated.map(
      (segment) => segment.trend.filter((point) => parseDbTime(point.at) <= at).slice(-1)[0]?.count,
    );
    return values.some((value) => value === undefined)
      ? []
      : [{ at, value: values.reduce<number>((sum, value) => sum + value!, 0) }];
  });
}

/**
 * Detection trend. Inline (default) it is a small shape beside the figure —
 * no axis, no caption; expanded (detail panel) it gains guides and endpoints.
 */
export function Sparkline({
  segments,
  expanded = false,
}: {
  segments: MonitoringSegment[];
  expanded?: boolean;
}) {
  const timezone = useDisplayTimezone();
  const samples = combinedTrend(segments);
  if (samples.length < 2)
    return expanded ? <p className="py-6 text-sm text-muted">Not enough data yet</p> : null;
  const max = Math.max(1, ...samples.map((point) => point.value));
  const span = samples[samples.length - 1].at - samples[0].at || 1;
  const points = samples
    .map((point) => `${((point.at - samples[0].at) / span) * 100},${30 - (point.value / max) * 26}`)
    .join(" ");
  return (
    <div className="min-w-0">
      <svg
        role="img"
        aria-label="Cumulative detections"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        className={`${expanded ? "h-32" : "h-6"} w-full text-data-detection`}
      >
        {expanded &&
          [4, 17, 30].map((y) => (
            <line
              key={y}
              x1="0"
              x2="100"
              y1={y}
              y2={y}
              className="text-border"
              stroke="currentColor"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        <polygon points={`0,32 ${points} 100,32`} fill="currentColor" opacity="0.1" />
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth={expanded ? 2 : 1.5}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {expanded && (
        <div className="mt-2 flex justify-between gap-4 text-xs tabular-nums text-muted">
          <span>
            {formatDateTime(new Date(samples[0].at).toISOString(), timezone)} ·{" "}
            {formatCount(samples[0].value)}
          </span>
          <span>
            {formatDateTime(new Date(samples[samples.length - 1].at).toISOString(), timezone)} ·{" "}
            {formatCount(samples[samples.length - 1].value)}
          </span>
        </div>
      )}
    </div>
  );
}
